/**
 * Public invitation API: the invitee's half of onboarding.
 *
 * **Anonymous by necessity**, so it bypasses the shared api client and its auth header the same way
 * `crmSubmissions` does for the public player form. The person calling this has no account yet,
 * which is the entire point, and `getCurrentUserToken` would only stall on a Firebase user who is
 * not there.
 *
 * Failures are thrown as `InvitationError` with a `kind` mapped from the status code, never from the
 * message text. The API answers every unusable invitation - missing, expired, revoked, already
 * accepted - with one identical string on purpose, so the page must not try to tell those apart
 * either.
 */

import { ApiError, del, get, post } from './api';
import type {
  AcceptInvitationBody,
  AcceptInvitationRequest,
  AcceptedInvitation,
  CreateInvitationRequest,
  Invitation,
  InvitationAdminFailureKind,
  InvitationFailureKind,
  InvitationPreview,
  InvitationPreviewBody,
} from '../types/invitations';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';

/**
 * Copy used when the server sent no message of its own. Each one has to stand on its own in front of
 * a coach who has never seen this product before, so none of them says "error".
 */
const DEFAULT_MESSAGES: Record<InvitationFailureKind, string> = {
  invalid: 'This invitation is invalid or has expired.',
  paused: 'This program is not active right now, so the invitation cannot be accepted yet.',
  invalid_input: 'Check the details you entered and try again.',
  already_registered: 'An account already exists for this email address.',
  rate_limited: 'Too many attempts. Wait a minute and try again.',
  unavailable: 'Something went wrong on our end. Your invitation link still works, so please try again.',
};

/** A refused public invitation call, carrying the state the page should render. */
export class InvitationError extends Error {
  readonly kind: InvitationFailureKind;

  constructor(kind: InvitationFailureKind, message: string) {
    super(message);
    this.name = 'InvitationError';
    this.kind = kind;
  }
}

/**
 * Status code to rendered state.
 *
 * Anything unmapped falls to `unavailable` rather than `invalid`: telling someone their link is dead
 * when the server merely hiccuped costs them the invitation, while the reverse only costs a retry.
 */
const failureKindFor = (status: number): InvitationFailureKind => {
  switch (status) {
    // Never a verdict on the token. The API checks the name and password rules before it looks the
    // token up, so an empty or malformed accept body answers 400 where it used to answer 404.
    // Reading that as `invalid` would retire a working invitation over a typo, and it is the one
    // status whose meaning changed when the token moved into the body.
    case 400:
      return 'invalid_input';
    case 403:
      return 'paused';
    case 404:
      return 'invalid';
    case 409:
      return 'already_registered';
    case 429:
      return 'rate_limited';
    default:
      return 'unavailable';
  }
};

const readFailure = async (response: Response): Promise<InvitationError> => {
  const kind = failureKindFor(response.status);

  const body: unknown = await response.json().catch(() => null);
  const message = (body as { message?: unknown } | null)?.message;

  return new InvitationError(
    kind,
    typeof message === 'string' && message.trim() ? message : DEFAULT_MESSAGES[kind]
  );
};

/**
 * **Both public endpoints are POSTs carrying the token in the BODY, and neither may carry it in the
 * URL again** - not a path segment, not a query string.
 *
 * The token is the whole credential. `Program.cs` logs the literal `{RequestPath}` to a file sink
 * retained for 90 days while a token lives 7, and `main.tsx` inits Sentry at a 1.0 trace rate with
 * no `beforeSend`, so every request URL is handed to a third party as well. Either sink gives a
 * reader a live invitation they can accept ahead of the invitee, arriving at whatever role it
 * carries, including admin, while the invitee sees only the generic "invalid or has expired".
 *
 * This is the same protection the accept page buys by keeping the token in the URL *fragment*, where
 * it never reaches a `Referer` or an access log. Lifting it out of the fragment and handing it back
 * in a request path threw that away, which is also why there is no transitional alias on the old
 * routes: an alias would reinstate exactly what this removes.
 *
 * A preview is a read and would otherwise be a GET. It is a POST for this reason alone.
 */
const PREVIEW_URL = `${API_BASE_URL}/api/public/invitations/preview`;
const ACCEPT_URL = `${API_BASE_URL}/api/public/invitations/accept`;

/**
 * The only request shape either public call uses: the token travels in the body, never the URL.
 *
 * The body is typed as the union of the two wire shapes rather than a `Record<string, string>`, so
 * the excess property check refuses a payload that has grown a `role` or an `organizationId` at the
 * point it is written. That is the same invariant `AcceptInvitationRequest` spends a paragraph
 * defending, and a bag of strings quietly opted out of it.
 */
const postToken = (url: string, body: InvitationPreviewBody | AcceptInvitationBody) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

/** A network failure is retryable and, crucially, leaves the invitation itself untouched. */
const networkFailure = () =>
  new InvitationError('unavailable', DEFAULT_MESSAGES.unavailable);

/**
 * Both endpoints are read through a guard rather than cast straight into their interface.
 *
 * Web and the API deploy on separate workflows off the same push, so a new client can meet an older
 * server. Without this a renamed field reads as "Join undefined", a blank read-only email, and
 * `undefined` handed to the sign-in prefill. Routing it to the retry state instead keeps a
 * deploy-skew window recoverable.
 */
const requireStrings = <T extends string>(
  body: unknown,
  keys: readonly T[]
): Record<T, string> => {
  const record = (body ?? {}) as Record<string, unknown>;
  const missing = keys.some(
    (key) => typeof record[key] !== 'string' || !(record[key] as string).trim()
  );

  if (missing) {
    throw new InvitationError('unavailable', DEFAULT_MESSAGES.unavailable);
  }

  return Object.fromEntries(keys.map((key) => [key, record[key]])) as Record<T, string>;
};

/**
 * Who sent the invitation and who it was sent to, so the page can say whose it is before asking for
 * a password.
 */
export const getInvitationPreview = async (
  token: string
): Promise<InvitationPreview> => {
  let response: Response;

  try {
    response = await postToken(PREVIEW_URL, { token });
  } catch {
    throw networkFailure();
  }

  if (!response.ok) {
    throw await readFailure(response);
  }

  return requireStrings(await response.json().catch(() => null), [
    'organizationName',
    'email',
  ]);
};

/**
 * Accepts the invitation and creates the account.
 *
 * The body is built field by field rather than spread from the caller, so a payload can never grow a
 * `role` or `organizationId` by accident. Both come from the invitation row server-side; the API's
 * DTO has no such field to bind them to. The `token` alongside them names *which* invitation is
 * being accepted and nothing more, so it leaves that invariant untouched.
 *
 * **No session is issued.** The invitee signs in normally afterwards.
 */
export const acceptInvitation = async (
  token: string,
  request: AcceptInvitationRequest
): Promise<AcceptedInvitation> => {
  let response: Response;

  try {
    response = await postToken(ACCEPT_URL, {
      token,
      firstName: request.firstName,
      lastName: request.lastName,
      password: request.password,
    });
  } catch {
    throw networkFailure();
  }

  if (!response.ok) {
    throw await readFailure(response);
  }

  return requireStrings(await response.json().catch(() => null), [
    'email',
    'organizationName',
  ]);
};

/* ────────────────────────────────────────────────────────────────────────────
   The admin's half: issuing and managing invitations.

   Authenticated, so unlike everything above these go through the shared api client and its Bearer
   token rather than a bare fetch. Everything is scoped to the caller's own organization server
   side; there is no organization id to pass and no way to ask about another tenant.
   ──────────────────────────────────────────────────────────────────────────── */

/** A refused admin invitation call. `invitation` is set only for a 502, which saves the row. */
export class InvitationAdminError extends Error {
  readonly kind: InvitationAdminFailureKind;
  readonly invitation: Invitation | null;

  constructor(
    kind: InvitationAdminFailureKind,
    message: string,
    invitation: Invitation | null = null
  ) {
    super(message);
    this.name = 'InvitationAdminError';
    this.kind = kind;
    this.invitation = invitation;
  }
}

/**
 * Reads a row defensively rather than casting the body into its interface.
 *
 * Web and the API deploy on separate workflows, so a new client can meet an older server. A row
 * missing a date it has not grown yet still lists, with that column reading "Unknown"; only an
 * unusable row - no id, no address, nothing to act on - is dropped.
 */
export const toInvitation = (body: unknown): Invitation | null => {
  const row = (body ?? {}) as Record<string, unknown>;

  const id = typeof row.id === 'string' && row.id ? row.id : null;
  const email = typeof row.email === 'string' && row.email ? row.email : null;

  if (!id || !email) return null;

  const text = (value: unknown) =>
    typeof value === 'string' && value.trim() ? value : null;

  return {
    id,
    email,
    role: text(row.role) ?? 'user',
    createdAt: text(row.createdAt),
    expiresAt: text(row.expiresAt),
    createdBy: text(row.createdBy),
    sentCount: typeof row.sentCount === 'number' ? row.sentCount : null,
    lastSentAt: text(row.lastSentAt),
    lastSendSucceeded:
      typeof row.lastSendSucceeded === 'boolean' ? row.lastSendSucceeded : null,
  };
};

/**
 * Status code to failure kind.
 *
 * A 404 means two different things depending on what was asked for, and the difference matters:
 * on the collection it means this API has no invitations endpoints at all (an older build), which
 * should hide the feature rather than report an error; on one invitation it means that invitation
 * is gone.
 */
const adminFailure = (
  error: unknown,
  missing: 'unsupported' | 'not_found'
): InvitationAdminError => {
  if (!(error instanceof ApiError)) {
    return new InvitationAdminError('failed', 'Something went wrong. Please try again.');
  }

  const message = error.message;

  switch (error.statusCode) {
    case 400:
      return new InvitationAdminError('invalid', message);
    case 404:
      return new InvitationAdminError(missing, message);
    case 409:
      return new InvitationAdminError('conflict', message);
    case 429:
      return new InvitationAdminError('throttled', message);
    case 502: {
      // The row was saved and comes back with the refusal, so a resend can be offered without
      // another round trip.
      const body = (error.response ?? {}) as { invitation?: unknown };
      return new InvitationAdminError('send_failed', message, toInvitation(body.invitation));
    }
    case 503:
      return new InvitationAdminError('no_transport', message);
    default:
      return new InvitationAdminError('failed', message);
  }
};

/**
 * The organization's pending invitations, newest first.
 *
 * Expired, accepted and revoked rows are not pending and are not returned, which is why the row
 * carries no status: every one of these is pending by construction, and a status field that is
 * always the same value only suggests a filter this screen does not have.
 */
export const listPendingInvitations = async (): Promise<Invitation[]> => {
  let body: unknown;

  try {
    body = await get<unknown>('/api/invitations');
  } catch (error) {
    throw adminFailure(error, 'unsupported');
  }

  if (!Array.isArray(body)) return [];

  return body
    .map(toInvitation)
    .filter((row): row is Invitation => row !== null);
};

/**
 * Invites an address and emails the link.
 *
 * Inviting an address that already has an open invitation re-arms that row rather than creating a
 * second one, so the caller can treat the result as "this is the live invitation for that address"
 * either way.
 */
export const createInvitation = async (
  request: CreateInvitationRequest
): Promise<Invitation> => {
  let body: unknown;

  try {
    // Field by field rather than spread from the caller, so the payload cannot grow an
    // organizationId or a userId by accident. The API binds only these two: a firstName or
    // lastName sent here is discarded silently, so it is not sent.
    body = await post<unknown>('/api/invitations', {
      email: request.email,
      role: request.role,
    });
  } catch (error) {
    throw adminFailure(error, 'unsupported');
  }

  const invitation = toInvitation(body);

  if (!invitation) {
    throw new InvitationAdminError('failed', 'Something went wrong. Please try again.');
  }

  return invitation;
};

/** Sends the invitation again, with a new token and a new expiry. */
export const resendInvitation = async (id: string): Promise<Invitation> => {
  let body: unknown;

  try {
    body = await post<unknown>(`/api/invitations/${encodeURIComponent(id)}/resend`);
  } catch (error) {
    throw adminFailure(error, 'not_found');
  }

  const invitation = toInvitation(body);

  if (!invitation) {
    throw new InvitationAdminError('failed', 'Something went wrong. Please try again.');
  }

  return invitation;
};

/** Withdraws an invitation. The link stops working immediately. */
export const revokeInvitation = async (id: string): Promise<void> => {
  try {
    await del<unknown>(`/api/invitations/${encodeURIComponent(id)}`);
  } catch (error) {
    throw adminFailure(error, 'not_found');
  }
};
